import React, { useState, useEffect } from "react";
import { useGenerateOtpMutation, useVerifyOtpMutation } from "../store/services/userApi";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Box,
    Paper,
    Radio,
    FormControlLabel,
    Typography,
    TextField,
    Button,
    Container
} from "@mui/material";

const SelectMobileForOtp = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const mobiles = location.state?.mobiles || [];
    const [successMsg, setSuccessMsg] = useState("");
    const [selectedMobile, setSelectedMobile] = useState("");
    const [otp, setOtp] = useState("");
    const [error, setError] = useState("");
    const [otpGenerated, setOtpGenerated] = useState(false);
    const [timeLeft, setTimeLeft] = useState(0);
    const [expiresAt, setExpiresAt] = useState(null);
    const username = localStorage.getItem("username");

    const [generateOtp, { isLoading: sendingOtp }] = useGenerateOtpMutation();
    const [verifyOtp, { isLoading: verifying }] = useVerifyOtpMutation();

    const handleGenerateOtp = async (e) => {
        e.preventDefault();
        setError("");

        if (!selectedMobile) {
            setError("Please select a mobile number");
            return;
        }

        try {
            const response = await generateOtp({ username, mobile: selectedMobile }).unwrap();
            setOtpGenerated(true);
            setExpiresAt(response.expiresAt);
            setOtp("");
            const normalized = selectedMobile.replace(/^\+91\s?/, '');
            const masked = '+91 ' + normalized.replace(/^(\d{3})\d{5}(\d{2})$/, '$1XXXXX$2');
            setSuccessMsg(`OTP has been sent to ${masked}`);
        } catch (err) {
            setError(err?.data?.message || "Failed to generate OTP");
        }
    };

    useEffect(() => {
        if (!expiresAt) return;

        const timer = setInterval(() => {
            const now = Date.now();
            const expiry = new Date(expiresAt).getTime();

            const remaining = Math.max(
                0,
                Math.floor((expiry - now) / 1000)
            );

            setTimeLeft(remaining);

            if (remaining <= 0) {
                setOtpGenerated(false);
                setOtp("");
                setSuccessMsg("");
                clearInterval(timer);
            }
        }, 1000);

        return () => clearInterval(timer);
    }, [expiresAt]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setError("");

        if (otp.length !== 6) {
            setError("OTP must be 6 digits");
            return;
        }

        try {
            const response = await verifyOtp({
                username,
                mobile: selectedMobile,
                otp,
            }).unwrap();

            localStorage.setItem("accessToken", response.accessToken);
            localStorage.setItem("refreshToken", response.refreshToken);
            localStorage.setItem("reseller", Boolean(response?.reseller));

            navigate("/");
        } catch (err) {
            setError(err?.data?.message || "OTP Verification failed");
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                minHeight="80vh"
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 2,
                        borderRadius: 2,
                        width: "100%",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Verify OTP
                    </Typography>

                    {error && (
                        <Typography color="error" sx={{ fontSize: "0.8rem", mb: 1 }}>
                            {error}
                        </Typography>
                    )}

                    <Box component="form" onSubmit={handleGenerateOtp} sx={{ mb: 2, display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                        {mobiles.map((mobile) => {
                            const maskedMobile = mobile.replace(/^\+91\s?/, '').replace(/^(\d{6})(\d{4})$/, 'XXXXXX$2');

                            return (
                                <FormControlLabel
                                    key={mobile}
                                    control={<Radio size="small" />}
                                    label={<Typography variant="body2">{maskedMobile}</Typography>}
                                    value={mobile}
                                    checked={selectedMobile === mobile}
                                    onChange={(e) => setSelectedMobile(e.target.value)}
                                    sx={{ my: 0.5 }}
                                />
                            );
                        })}

                        {!otpGenerated && (
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="small"
                                sx={{ mt: 1, py: 0.8, fontSize: "0.875rem" }}
                                disabled={sendingOtp}
                            >
                                {sendingOtp ? "Sending..." : "Generate OTP"}
                            </Button>
                        )}
                    </Box>

                    {otpGenerated && timeLeft > 0 && (
                        <Box component="form" onSubmit={handleVerifyOtp}>
                            {successMsg && (
                                <Typography color="success.main" sx={{ fontSize: "0.8rem", mb: 1 }}>
                                    {successMsg}
                                </Typography>
                            )}
                            <Typography variant="subtitle2" gutterBottom>
                                Enter 6-digit OTP
                                <span style={{ color: 'red' }}>
                                    (resend after {formatTime(timeLeft)})
                                </span>
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                type="text"
                                inputProps={{ maxLength: 6 }}
                                value={otp}
                                onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                sx={{ mb: 1 }}
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                size="small"
                                sx={{ py: 0.8, fontSize: "0.875rem" }}
                                disabled={verifying}
                            >
                                {verifying ? "Verifying..." : "Submit"}
                            </Button>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default SelectMobileForOtp;